import { useCallback, useEffect, useState } from "react";
import { useApi } from "../../hooks/useApi";
import type { ItemChange, ItemState, NewOnboardingStep, OnboardingDetail, OnboardingDetailItem } from "../../types/onboarding";

// What the panel changes on a step; a state move is sent with the state the page showed, so a stale page's
// move is refused instead of overwriting one that landed first.
export type StepEdit = { state: ItemState } | { notes: string } | { details: Record<string, unknown> };
const toItemChange = (item: OnboardingDetailItem, edit: StepEdit): ItemChange => ("state" in edit ? { ...edit, fromState: item.state } : edit);

export function useOnboarding(onboardingId: number) {
  const { get, loading } = useApi();
  const { patch, post } = useApi();
  const [onboarding, setOnboarding] = useState<OnboardingDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loadedId, setLoadedId] = useState(onboardingId);
  if (loadedId !== onboardingId) {
    setLoadedId(onboardingId);
    setOnboarding(null);
    setError(null);
  }

  useEffect(() => {
    let current = true;
    get<OnboardingDetail>(`/api/onboardings/${onboardingId}`)
      .then((res) => { if (current) { setOnboarding(res); setError(null); setSaving(false); } })
      .catch((err: Error) => { if (current) { setOnboarding(null); setError(err.message); setSaving(false); } });
    return () => { current = false; };
  }, [get, onboardingId, version]);

  // Every write reloads the checklist, so the page shows what was saved even when part of it failed, and stays
  // saving until the reload lands. Resolves to the error for the caller to show where it was made, or null.
  const run = useCallback(async (request: () => Promise<unknown>): Promise<string | null> => {
    setSaving(true);
    try {
      await request();
      return null;
    } catch (err) {
      return (err as Error).message;
    } finally {
      setVersion((v) => v + 1);
    }
  }, []);

  // One request per item, so asking the client for several steps moves each of them. The reload waits for
  // every request, so a step saved after another one failed doesn't show as still to do.
  const change = useCallback((items: OnboardingDetailItem[], edit: StepEdit) =>
    run(async () => {
      const results = await Promise.allSettled(items.map((item) => patch(`/api/onboardings/items/${item.itemId}`, toItemChange(item, edit))));
      const failed = results.find((r): r is PromiseRejectedResult => r.status === "rejected");
      if (failed) throw failed.reason;
    }), [run, patch]);

  const addStep = useCallback((step: NewOnboardingStep) =>
    run(() => post(`/api/onboardings/${onboardingId}/items`, step)), [run, post, onboardingId]);

  return { onboarding, error, loading, change, addStep, saving };
}
